import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { makeRequest2 } from "../../../../utils/makeRequest";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../../component/layout";
import styles from "./powerInvoice.module.css";
import { useFacilityStore } from "../../../../app/z-store/store";
import PowerInvoiceActionsDropdown from "./powerInvoiceActionsDropdown";
import { getPowerInvoice } from "../../../../utils/urls";


const PowerInvoicePage = () => {
  const routerLocation = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState(null);
  const [mobileDetails, setMobileDetails] = useState(null);
  const [billerAddress, setBillerAddress] = useState(null);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [facilityLogo, setFacilityLogo] = useState("");
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef(null);
  const facilityId = useFacilityStore((state) => state.facilityId);

  const { invoiceId, invoiceData: passedInvoiceData } = location.state || {};

  useEffect(() => {

    if (passedInvoiceData) {
      setInvoice(passedInvoiceData);
      setLoading(false);
      loadExtras(passedInvoiceData);
      getFacilityLogo();
      return;
    }

    if (invoiceId && facilityId) {
      fetchInvoiceData();
    } else {
      setLoading(false);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Missing invoice ID or facility ID" });
    }
  }, [invoiceId, facilityId, passedInvoiceData]);


  const getFacilityLogo = async () => {
    try {
      const facilityFromState = routerLocation.state?.facility;

      if (facilityFromState?.logo) {
        const fullLogoUrl = facilityFromState.logo;
        localStorage.setItem("selectedFacilityLogo", fullLogoUrl);
        setFacilityLogo(fullLogoUrl);
      } else {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        if (storedLogo) {
          setFacilityLogo(storedLogo);
        } else {
          setFacilityLogo("");
        }
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      setFacilityLogo("");
    }
  };

  // ─── Fetch invoice ───────────────────────────────────────────────────────────
  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getPowerInvoice}/${invoiceId}?facilityId=${facilityId}`,
        "GET",
        {}
      );

      if (response.success) {
        const invoiceData = response.data?.data?.invoice || response.data?.invoice;
        if (invoiceData) {
          setInvoice(invoiceData);
          loadExtras(invoiceData);
        } else {
          toast.current?.show({ severity: "error", summary: "Error", detail: "Unexpected invoice structure" });
        }
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: response.message || "Failed to fetch invoice" });
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Error", detail: error.message || "Unexpected error" });
    } finally {
      setLoading(false);
    }
  };

  // ─── Load payment details + biller address ───────────────────────────────────
  const loadExtras = async (inv) => {
    try {
      setLoadingExtras(true);
      await Promise.all([
        loadBillerAddress(inv),
        loadBankDetails(inv),
        loadMobileDetails(inv),
      ]);
    } finally {
      setLoadingExtras(false);
    }
  };

  // ─── Biller address ──────────────────────────────────────────────────────────
  const loadBillerAddress = async (inv) => {
    if (inv?.billerAddress?.name) {
      setBillerAddress(inv.billerAddress);
      return;
    }
    if (inv?.billerAddressId && typeof inv.billerAddressId === 'object' && inv.billerAddressId.name) {
      setBillerAddress(inv.billerAddressId);
      return;
    }
    if (inv?.billerAddressId && typeof inv.billerAddressId === 'string') {
      try {
        const r = await makeRequest2(
          `/api/app/settings_management/get_biller_address_by_id/${inv.facilityId}/${inv.billerAddressId}`,
          "GET",
          {}
        );
        if (r.success && r.data) {
          setBillerAddress(r.data.data || r.data);
        }
      } catch (e) {
        console.error("Biller address fetch error:", e);
      }
    }
  };

  // ─── Bank payment details ────────────────────────────────────────────────────
  const loadBankDetails = async (inv) => {
    const bp = inv?.paymentMethods?.bankPayment;
    if (!bp?.status) return;

    if (bp.paymentId && typeof bp.paymentId === 'object' && bp.paymentId.bankName) {
      setBankDetails(bp.paymentId);
      return;
    }

    const bankId = typeof bp.paymentId === 'string' ? bp.paymentId : null;
    if (!bankId) return;

    try {
      const r = await makeRequest2(
        `/api/app/settings_management/get_bank_details_by_id/${inv.facilityId}/${bankId}`,
        "GET",
        {}
      );
      if (r.success && r.data) {
        setBankDetails(r.data.data || r.data);
      }
    } catch (e) {
      console.error("Bank details fetch error:", e);
    }
  };

  // ─── Mobile / M-Pesa payment details ────────────────────────────────────────
  const loadMobileDetails = async (inv) => {
    const mp = inv?.paymentMethods?.mobilePayment;
    if (!mp?.status) return;

    // shortCode already enriched onto the invoice by the backend gateway
    if (mp.shortCode) {
      setMobileDetails({ shortCode: mp.shortCode });
      return;
    }

    // paymentId already populated as a full object
    if (mp.paymentId && typeof mp.paymentId === 'object' && mp.paymentId.shortCode) {
      setMobileDetails(mp.paymentId);
      return;
    }

    // Last resort: fetch by raw ID string
    const mobileId = typeof mp.paymentId === 'string' ? mp.paymentId : null;
    if (!mobileId) return;

    try {
      const r = await makeRequest2(
        `/api/app/settings_management/get_facility_payment_details/${inv.facilityId}/${mobileId}`,
        "GET",
        {}
      );
      if (r.success && r.data) {
        setMobileDetails(r.data.data || r.data);
      }
    } catch (e) {
      console.error("Mobile details fetch error:", e);
    }
  };

  // ─── PDF ──────────────────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    const element = document.getElementById("printable-power-invoice");
    if (!element) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "PDF element not found" });
      return;
    }

    const filename = `Power_Invoice_${invoice?.invoiceNumber || "unknown"}_${new Date().toISOString().replace(/[:.]/g, "-")}.pdf`;
    try {
      setLoading(true);
      toast.current?.show({ severity: "info", summary: "Processing", detail: "Generating PDF..." });

      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = 210, pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
        heightLeft -= pageHeight;
      }
      pdf.save(filename);
      toast.current?.show({ severity: "success", summary: "Success", detail: "PDF downloaded!" });
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate PDF." });
    } finally {
      setLoading(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      const day = d.getDate();
      const sfx = (n) => {
        if (n > 3 && n < 21) return "th";
        switch (n % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
      };
      return `${day}${sfx(day)} ${d.toLocaleDateString("en-US", { month: "short" })} ${d.getFullYear()}`;
    } catch { return "Invalid Date"; }
  };

  const formatCurrency = (amount) => {
    const symbol = invoice?.currency?.symbol || "KSh";
    return `${symbol} ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatAmount = (amount) => {
    const code = invoice?.currency?.code || "KES";
    return `${code} ${parseFloat(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ─── Charge breakdown ─────────────────────────────────────────────────────────
  const getChargeBreakdown = () => {
    const c = invoice?.powerCharges || invoice?.charges || {};
    return [
      { key: "energyCharge", label: "Energy Charge", value: c.energyCharge ?? 0 },
      { key: "fuelCostCharge", label: "Fuel Cost Charge", value: c.fuelCostCharge ?? 0 },
      { key: "forexAdjustment", label: "Foreign Exchange Adjustment", value: c.forexAdjustment ?? 0 },
      { key: "inflationAdjustment", label: "Inflation Adjustment", value: c.inflationAdjustment ?? 0 },
      { key: "waterResourceManagementLevy", label: "Water Resource Management Levy", value: c.waterResourceManagementLevy ?? 0 },
      { key: "energyRegulatoryLevy", label: "Energy Regulatory Levy (ERC)", value: c.energyRegulatoryLevy ?? 0 },
      { key: "ruralElectrificationLevy", label: `Rural Electrification Levy (REP ${c.repPercentage ?? 5}%)`, value: c.ruralElectrificationLevy ?? 0 },
      { key: "powerFactorSurcharge", label: "Power Factor Surcharge", value: c.powerFactorSurcharge ?? 0 },
      { key: "valueAddedTax", label: `VAT ${c.vatPercentage ?? 16}%`, value: c.valueAddedTax ?? 0 },
    ].filter((row) => row.value > 0);
  };

  const getBalanceBroughtForward = () => invoice?.balanceBroughtForward || 0;
  const calculateSubtotal = () => getChargeBreakdown().reduce((s, r) => s + r.value, 0);
  const calculateInvoiceTotal = () => calculateSubtotal() + getBalanceBroughtForward();
  const calculateRemainingBalance = () => Math.max(0, calculateInvoiceTotal() - (invoice?.amountPaid || 0));

  const getBalanceInfo = (balance) => {
    if (balance > 0) return { label: "B/B FORWARD", cls: "bfDebit", sign: "" };
    if (balance < 0) return { label: "B/B FORWARD", cls: "bfCredit", sign: "-" };
    return { label: "B/B FORWARD", cls: "bfNeutral", sign: "" };
  };

  // ─── Receipt helpers ───────────────────────────────────────────────────────────
  const isAllowedType = (type) => {
    const t = (type || "").toLowerCase();
    return ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"].some(
      (a) => t === a || t.includes(a.replace("-", ""))
    );
  };

  const viewReceipt = (paymentData) => {
    if (!isAllowedType(paymentData.type)) {
      toast.current?.show({ severity: "info", summary: "Info", detail: "Receipt not available for this payment type" });
      return;
    }
    navigate(`/facility/levy_management/receipt/${paymentData._id}`, { state: { receipt: paymentData, invoice } });
  };

  const receiptActionTemplate = (rowData) => (
    <Button
      icon="fas fa-eye"
      className="p-button-sm p-button-text"
      onClick={() => viewReceipt(rowData)}
      disabled={!isAllowedType(rowData.type)}
      tooltip={isAllowedType(rowData.type) ? "View Receipt" : "Receipt not available"}
      tooltipOptions={{ position: "left" }}
    />
  );

  // ─── Payment details block ─────────────────────────────────────────────────────
  const renderPaymentDetails = () => {
    if (loadingExtras) {
      return (
        <div className={styles.paymentDetails}>
          <h6 className={styles.detailsTitle}>Payment Details:</h6>
          <p>Loading...</p>
        </div>
      );
    }

    const hasBankPayment = invoice?.paymentMethods?.bankPayment?.status && bankDetails;
    const hasMobilePayment = invoice?.paymentMethods?.mobilePayment?.status && mobileDetails;

    if (!hasBankPayment && !hasMobilePayment) {
      return (
        <div className={styles.paymentDetails}>
          <h6 className={styles.detailsTitle}>Payment Details:</h6>
          <h6 className={styles.detailsText}>Account: {invoice?.accountNumber || "N/A"}</h6>
        </div>
      );
    }

    return (
      <div className={styles.paymentDetails}>

        {/* ── Bank Payment ── */}
        {hasBankPayment && (
          <>
            <h6 className={styles.detailsTitle}>Bank Payment Details:</h6>
            <h6 className={styles.detailsText}>Bank: {bankDetails.bankName || "N/A"}</h6>
            <h6 className={styles.detailsText}>Account Name: {bankDetails.accountName || "N/A"}</h6>
            <h6 className={styles.detailsText}>Account Number: {bankDetails.accountNumber || "N/A"}</h6>
            <h6 className={styles.detailsText}>Branch: {bankDetails.branchName || "N/A"}</h6>
            {bankDetails.branchCode && (
              <h6 className={styles.detailsText}>Branch Code: {bankDetails.branchCode}</h6>
            )}
            {bankDetails.swiftCode && (
              <h6 className={styles.detailsText}>Swift Code: {bankDetails.swiftCode}</h6>
            )}
            {bankDetails.bankPaybill && (
              <h6 className={styles.detailsText}>Paybill: {bankDetails.bankPaybill}</h6>
            )}
          </>
        )}

        {/* Divider when both methods are active */}
        {hasBankPayment && hasMobilePayment && (
          <hr style={{ margin: "8px 0", borderColor: "#ccc" }} />
        )}

        {/* ── Mobile / M-Pesa Payment ── */}
        {hasMobilePayment && (
          <>
            <h6 className={styles.detailsTitle}>Pay To M-pesa:</h6>
            <h6 className={styles.detailsText}>
              Paybill Number: {mobileDetails.shortCode || mobileDetails.paybillNumber || "N/A"}
            </h6>
            <h6 className={styles.detailsText}>
              Account Number: {invoice?.accountNumber || "Use Invoice Number"}
            </h6>
            
          </>
        )}
      </div>
    );
  };

  // ─── Biller address helpers ────────────────────────────────────────────────────
  const getBillerName = () => billerAddress?.companyName || billerAddress?.name || invoice?.billerAddress?.name || "Company Name";
  const getBillerAddress = () => billerAddress?.address || invoice?.billerAddress?.address || "Company Address";
  const getBillerPhone = () => billerAddress?.phone || invoice?.billerAddress?.phone || "N/A";
  const getBillerEmail = () => billerAddress?.email || invoice?.billerAddress?.email || "";
  const getBillerCity = () => billerAddress?.city || invoice?.billerAddress?.city || "";

  // ─── Loading / error states ────────────────────────────────────────────────────
  if (loading && !invoice) {
    return <Layout><Toast ref={toast} /><div className={styles.loading}>Loading...</div></Layout>;
  }

  if (!invoice) {
    return (
      <Layout>
        <Toast ref={toast} />
        <div className={styles.pageContainer}>
          <div className={styles.errorMessage}>No invoice data available.</div>
          <Link to="/facility/utility_management/meter_management/power_meters" state={{ activeTab: "invoices" }}>
            <i className="ti ti-arrow-narrow-left"></i> Back
          </Link>
        </div>
      </Layout>
    );
  }

  // ─── Derived values ────────────────────────────────────────────────────────────
  const c = invoice?.powerCharges || invoice?.charges || {};
  const chargeBreakdown = getChargeBreakdown();
  const balanceBF = getBalanceBroughtForward();
  const balanceInfo = getBalanceInfo(balanceBF);
  const subtotal = calculateSubtotal();
  const invoiceTotal = calculateInvoiceTotal();
  const remainingBalance = calculateRemainingBalance();
  const totalPayments = invoice?.amountPaid || 0;

  const readBtnBase = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: "0.5rem 1rem", borderRadius: "20px", fontSize: "0.875rem",
    fontWeight: "600", border: "none", cursor: "pointer", marginLeft: "0.75rem"
  };

  return (
    <Layout>
      <Toast ref={toast} />
      <div className={styles.pageContainer}>

        {/* Action bar */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link to="/facility/utility_management/meter_management/power_meters" state={{ activeTab: "invoices" }}>
              <i className="ti ti-arrow-narrow-left"></i> Back
            </Link>
            <div style={{ display: "inline-block", marginLeft: 20 }}>
              {invoice?.viewStatus?.isOpened
                ? <button style={{ ...readBtnBase, backgroundColor: "#28a745", color: "#fff" }}><i className="fas fa-check-circle me-1"></i> Read</button>
                : <button style={{ ...readBtnBase, backgroundColor: "#ffc107", color: "#212529" }}><i className="fas fa-exclamation-circle me-1"></i> Unread</button>
              }
            </div>
          </div>
          <div className={styles.actionCardRight}>
            <PowerInvoiceActionsDropdown
              invoice={invoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              setShowReceiptsDialog={setShowReceiptsDialog}
              loading={loading}
            />
          </div>
        </div>

        {/* Receipts dialog */}
        <Dialog
          header="Payment Receipts"
          visible={showReceiptsDialog}
          style={{ width: "50vw" }}
          onHide={() => setShowReceiptsDialog(false)}
          resizable={false}
        >
          <DataTable
            value={invoice?.reconciliationHistory || []}
            responsiveLayout="scroll"
            emptyMessage="No payment records found"
            className="p-datatable-sm"
            paginator rows={5} rowsPerPageOptions={[5, 10, 20]}
          >
            <Column field="date" header="Date" body={(r) => formatDate(r.date)} sortable />
            <Column field="amount" header="Amount" body={(r) => formatAmount(r.amount)} sortable />
            <Column field="type" header="Type" sortable body={(r) => {
              const t = (r.type || "").toLowerCase();
              if (t === "cash") return <span>Cash</span>;
              if (t.includes("mpesa")) return <span>M-Pesa</span>;
              if (t.includes("bank")) return <span>Bank Transfer</span>;
              if (t === "cheque" || t === "check") return <span>Cheque</span>;
              return <span>{r.type || "Unknown"}</span>;
            }} />
            <Column field="paymentReference" header="Reference" sortable />
            <Column field="paymentCompletion" header="Status" sortable body={(r) => (
              <span className={`badge ${r.paymentCompletion === "Completed" ? "bg-success" :
                r.paymentCompletion === "Partial" ? "bg-warning" :
                  r.paymentCompletion === "Overpaid" ? "bg-info" : "bg-secondary"
                }`}>{r.paymentCompletion}</span>
            )} />
            <Column body={receiptActionTemplate} exportable={false} style={{ width: "4rem" }} />
          </DataTable>
        </Dialog>

        {/* ─────────── INVOICE ─────────── */}
        <div className={styles.invoiceContainer}>
          <div id="printable-power-invoice" className={styles.invoiceContent}>

            {/* Status ribbon */}
            <div className={styles.statusBanner} data-status={invoice?.status?.toLowerCase()}>
              <span>{invoice?.status || "Unpaid"}</span>
            </div>

            {/* HEADER */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1 className={styles.invoiceTitleInHeader}>
                    ELECTRICITY<br />INVOICE
                  </h1>
                  {facilityLogo && (
                    <div className={styles.logoContainer}>
                      <img src={facilityLogo} alt="Company Logo" className={styles.companyLogo} />
                    </div>
                  )}
                </div>

                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>{invoice?.invoiceNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>{invoice?.accountNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>TARIFF</span>
                    <span className={styles.value}>{c.tariff || invoice?.tariff || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dateIssued)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dueDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* BODY */}
            <div className={styles.invoiceBody}>

              {/* FROM / BILL TO / TOTAL DUE */}
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>

                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      <div style={{ fontWeight: 600 }}>{getBillerName()}</div>
                      <div>{getBillerAddress()}</div>
                      {getBillerCity() && <div>{getBillerCity()}</div>}
                      <div>{getBillerPhone()}</div>
                      {getBillerEmail() && <div>{getBillerEmail()}</div>}
                    </div>
                  </div>

                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{invoice?.CustomerInfo?.fullName || "N/A"}</div>
                      <div>Unit: {invoice?.unitName || "N/A"}</div>
                      <div>Account: {invoice?.accountNumber || "N/A"}</div>
                    </div>
                  </div>

                  <div className={styles.totalSection}>
                    <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                    <div className={styles.totalAmount}>{formatCurrency(remainingBalance)}</div>
                  </div>

                </div>
              </div>

              {/* METER DETAILS */}
              <div className={styles.itemsGrid}>
                <h2 className={styles.sectionHeading}>METER DETAILS</h2>

                <div className={styles.consumptionBlock}>
                  <div className={styles.detailItem}>
                    <span className={styles.infoLabel}>Consumption Period:</span>
                    <span>
                      {invoice?.consumptionPeriod
                        ? `${formatDate(invoice.consumptionPeriod.startDate)} - ${formatDate(invoice.consumptionPeriod.endDate)}`
                        : "N/A"}
                    </span>
                  </div>
                  <div className={styles.detailItem} style={{ marginTop: "0.3rem" }}>
                    <span className={styles.infoLabel}>Tariff:</span>
                    <span>{c.tariff || invoice?.tariff || "N/A"}</span>
                  </div>
                  <div className={styles.detailItem} style={{ marginTop: "0.3rem" }}>
                    <span className={styles.infoLabel}>Billing Month:</span>
                    <span>{c.yearMonth || invoice?.yearMonth || "N/A"}</span>
                  </div>
                </div>

                <div className={styles.meterDetailsTable} style={{ "--col-count": 5 }}>
                  <div className={styles.tableHeader}>
                    <div className={styles.tableHeaderCell}>Meter #</div>
                    <div className={styles.tableHeaderCell}>Prev Reading (kWh)</div>
                    <div className={styles.tableHeaderCell}>Current Reading (kWh)</div>
                    <div className={styles.tableHeaderCell}>Consumed Units</div>
                    <div className={styles.tableHeaderCell}>Power Factor</div>
                  </div>
                  <div className={styles.tableRow}>
                    <div className={styles.tableCell}>{invoice?.meterNumber || "N/A"}</div>
                    <div className={styles.tableCell}>{invoice?.meterReadings?.previousReading ?? "N/A"}</div>
                    <div className={styles.tableCell}>{invoice?.meterReadings?.currentReading ?? "N/A"}</div>
                    <div className={styles.tableCell}>{invoice?.meterReadings?.usage != null ? `${invoice.meterReadings.usage} units` : "N/A"}</div>
                    <div className={styles.tableCell}>{invoice?.powerFactor ?? "N/A"}</div>
                  </div>
                </div>
              </div>

              {/* BILLING DETAILS */}
              <div className={styles.billingSection}>
                <h2 className={styles.sectionHeading}>BILLING DETAILS</h2>

                <div className={styles.calculationsWrapper}>

                  {chargeBreakdown.map((row) => (
                    <div key={row.key} className={styles.calcRow}>
                      <span className={styles.calcLabel}>{row.label.toUpperCase()}</span>
                      <span className={styles.calcValue}>{formatCurrency(row.value)}</span>
                    </div>
                  ))}

                  <div className={styles.calcDivider} />

                  <div className={styles.calcRow}>
                    <span className={styles.calcLabel}>CHARGE</span>
                    <span className={styles.calcValue}>{formatCurrency(subtotal)}</span>
                  </div>

                  {balanceBF !== 0 && (
                    <div className={styles.calcRow}>
                      <span className={`${styles.calcLabel} ${styles[balanceInfo.cls]}`}>{balanceInfo.label}</span>
                      <span className={`${styles.calcValue} ${styles[balanceInfo.cls]}`}>{balanceInfo.sign}{formatCurrency(Math.abs(balanceBF))}</span>
                    </div>
                  )}

                  <div className={styles.calcDivider} />

                  <div className={styles.calcRow}>
                    <span className={`${styles.calcLabel} ${styles.invoiceTotalLabel}`}>INVOICE TOTAL</span>
                    <span className={`${styles.calcValue} ${styles.invoiceTotalLabel}`}>{formatCurrency(invoiceTotal)}</span>
                  </div>

                  {totalPayments > 0 && (
                    <div className={styles.calcRow}>
                      <span className={styles.calcLabel}>PAYMENTS RECEIVED</span>
                      <span className={`${styles.calcValue} ${styles.creditAmount}`}>-{formatCurrency(totalPayments)}</span>
                    </div>
                  )}

                  <div className={styles.calcDivider} />

                  <div className={`${styles.calcRow} ${styles.totalDueRow}`}>
                    <span className={styles.calcLabel}>TOTAL DUE</span>
                    <span className={`${styles.calcValue} ${styles.totalDueValue}`}>{formatCurrency(remainingBalance)}</span>
                  </div>

                </div>
              </div>

              {/* FOOTER */}
              <div className={styles.footer} style={{ backgroundColor: "#f0f0f0" }}>
                <div className="row">
                  <div className="col-sm-6 mt-2 px-5">
                    {renderPaymentDetails()}
                  </div>
                  <div className="col-sm-6">
                    <div className={styles.signatureSection}>
                      <div className={styles.signatureBox}>
                        <img
                          src={
                            facilityId === "685cdf7108c6b8a9e029794d"
                              ? "/sig_vollaire.png"
                              : facilityId === "68c7c413035b2bceba18a1a1"
                                ? "/altura_sign.png"
                                : "/signature.png"
                          }
                          alt="Digital Signature"
                          style={{ width: "250px", height: "100px", objectFit: "contain", marginBottom: "0px", marginLeft: "25px" }}
                        />
                        <p style={{ textAlign: "center", margin: "5px 0" }}>Authorized Signature</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 pt-2">
                    <div className={styles.centeredText}>
                      <p style={{ textAlign: "center" }}>
                        {invoice?.paymentTerms || "To complete your payment, kindly use the provided payment details above."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default PowerInvoicePage;